import { useContext, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import {
  InviteToApplyHook_InviteToApplyMutationVariables,
  useInviteToApplyHook_GetConversationDataQuery,
  useInviteToApplyHook_InviteToApplyMutation,
} from '../types/GraphqlTypes';
import { ModalType } from '../types/ModalType';
import { APPLICATIONS_UPSELL_MODAL_COOKIE } from '../components/Modals/ApplicationsUpsellModal';
import { AppContext } from '../contexts/AppContextProvider';
import useModal from './useModal';
import getApplicationActionText, { ACTION_TEXT } from '../utils/getApplicationActionText';
import { logApolloError } from '../utils/errorUtils';
import { appMarkerLogger } from '../bootstrap';

const logger = appMarkerLogger.child({ name: 'useInviteToApply' });

type UseInviteToApplyArgs = {
  conversationId: string;
  listingAlias: string;
  shouldShowApplicationUpsellModal: boolean;
};

/**
 *
 * @description useInviteToApply offers helpers to perform sending an application to a lead
 */
export default ({ conversationId, listingAlias, shouldShowApplicationUpsellModal }: UseInviteToApplyArgs) => {
  const { paths } = useContext(AppContext);
  const { setActiveModal } = useModal();
  const [cookies] = useCookies([APPLICATIONS_UPSELL_MODAL_COOKIE]);

  const { data: conversationData, loading: isLoading } = useInviteToApplyHook_GetConversationDataQuery({
    variables: {
      listingAlias,
      conversationId,
    },
    fetchPolicy: 'cache-only',
  });

  const [inviteToApplyMutation] = useInviteToApplyHook_InviteToApplyMutation();

  const {
    applicationAlreadySent,
    applicationCompleted,
    attributes,
    inquiry,
    isApplicationsAccepted,
    isApplicationsAllowed,
    isConversationBlockedByZillow,
    referenceEmail,
    referenceName,
    status,
  } = conversationData?.ZRM_getConversationById || {};
  const { isArchived, isSpam } = status || {};
  const { applicationsUrl } = paths;
  const applicationMarkedSent = attributes?.applicationMarkedSent;
  const name = inquiry?.name || referenceName || '';
  const viewApplicationsForListingUrl = `${applicationsUrl}?propertyId=${listingAlias}`;

  const [actionText, setActionText] = useState<ACTION_TEXT | null>(null);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [helperText, setHelperText] = useState({
    text: '',
    isFailure: false,
  });

  useEffect(() => {
    // Reset helper text when conversationId changes
    setHelperText({
      text: '',
      isFailure: false,
    });
  }, [conversationId]);

  useEffect(() => {
    const applicationActionText = getApplicationActionText({
      isApplicationCompleted: !!applicationCompleted,
      hasSentApplication: !!(applicationAlreadySent || applicationMarkedSent),
    });
    setActionText(applicationActionText);
  }, [applicationAlreadySent, applicationCompleted, applicationMarkedSent]);

  const handleInviteToApplyClick = async (isRetry = false): Promise<void> => {
    setIsSendingInvite(true);
    setHelperText({ text: '', isFailure: false });

    const variables: InviteToApplyHook_InviteToApplyMutationVariables = {
      listingId: listingAlias || '',
      renterFirstName: name?.split(' ')[0],
      renterLastName: name?.split(' ')[1],
      toEmail: referenceEmail || '',
      resendEmail: actionText === ACTION_TEXT.RESEND_APPLICATION,
    };

    logger.info(variables, 'Handling invite to apply');

    await inviteToApplyMutation({
      variables,
      onCompleted() {
        setHelperText({ text: 'Invite sent!', isFailure: false });

        // Allow landlords a chance to enable applications if it's not enabled
        // for their listing by showing them the Applications Upsell Modal.
        // The modal will only appear once.
        if (shouldShowApplicationUpsellModal && !isApplicationsAccepted && !cookies[APPLICATIONS_UPSELL_MODAL_COOKIE]) {
          setActiveModal(ModalType.ApplicationsUpsellModal);
        }
      },
      update(cache) {
        cache.modify({
          id: cache.identify({
            __typename: 'ZRM_Conversation',
            conversationId,
          }),
          fields: {
            applicationAlreadySent() {
              return true;
            },
          },
        });
      },
      onError(error) {
        if (!isRetry) {
          return handleInviteToApplyClick(true);
        }

        logApolloError(error, {
          details: 'useInviteToApply - Error on invite to apply',
          ctaText: actionText,
          ...variables,
        });
        setHelperText({
          text: 'There was an error sending your invitation. Please try again.',
          isFailure: true,
        });
      },
    });

    setIsSendingInvite(false);
  };

  return {
    actionText,
    handleInviteToApplyClick,
    helperText,
    isApplicationsAllowed,
    isConversationArchived: isArchived,
    isConversationBlockedByZillow,
    isConversationMarkedAsSpam: isSpam,
    isLoading,
    isSendingInvite,
    viewApplicationsForListingUrl,
  };
};
